import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import {
  MobileNumberSetupForm,
  type ExpertMobileNumberSetupSource,
} from '@/components/MobileNumberSetupForm';
import { MobileNumberSetupSheet } from '@/components/MobileNumberSetupSheet';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { userIdAtom } from '@/services/globalState';

export type { ExpertMobileNumberSetupSource };

interface ExpertMobileNumberSetupModalProps {
  isOpen: boolean;
  source: ExpertMobileNumberSetupSource;
  onClose: () => void;
  onPhoneNumberSet?: () => void;
}

/**
 * Standalone overlay wrapper around `MobileNumberSetupForm`, presented via
 * `MobileNumberSetupSheet` (a centered modal on web, a bottom action sheet
 * on native). Use for surfaces triggered from a plain screen, not from
 * inside another modal — the web reference wizard renders the form inline
 * instead, to avoid stacking modals.
 *
 * Behavior is source-specific:
 * - `first_message`: the goal is the expert receiving SMS, so saving
 *   also enables `newMessage` SMS. It surfaces whenever SMS won't reach
 *   the expert — no number, OR a number on file with `newMessage` SMS
 *   off — pre-filling the existing number so they can just confirm.
 * - `notification_settings`: captures a number only; the user toggles
 *   SMS in the settings list, so it does not auto-enable. It is opened
 *   only when no number is on file, and hides once one exists.
 */
export function ExpertMobileNumberSetupModal({
  isOpen,
  source,
  onClose,
  onPhoneNumberSet,
}: ExpertMobileNumberSetupModalProps) {
  const userId = useAtomValue(userIdAtom);

  const { data: userData } = useQuery(getCurrentUserV2QueryOptions(userId));

  const handleComplete = useCallback(() => {
    onPhoneNumberSet?.();

    onClose();
  }, [onPhoneNumberSet, onClose]);

  const mobilePhoneSliced = userData?.mobilePhone?.replace('+1', '') || '';

  const hasPhone = mobilePhoneSliced.length > 0;

  const isSettings = source === 'notification_settings';

  const newMessageSmsEnabled = Boolean(userData?.notificationsPreferences?.newMessageSms);

  const smsWillReach = isSettings ? hasPhone : hasPhone && newMessageSmsEnabled;

  if (smsWillReach) {
    return null;
  }

  return (
    <MobileNumberSetupSheet isOpen={isOpen} onClose={onClose}>
      <MobileNumberSetupForm
        source={source}
        enableSmsOnSave={!isSettings}
        defaultMobilePhone={mobilePhoneSliced}
        requireDirty={!hasPhone}
        onComplete={handleComplete}
        onSkip={onClose}
      />
    </MobileNumberSetupSheet>
  );
}
